import { Component, ElementRef, HostListener } from '@angular/core';


export interface Resource {
  displayName?: string;
  employeeName?: string;
  businessName?: string;
  phone?: string;
  email?: string;
  category?: string;
}

interface ServiceStyle {
  background: string;
  color: string;
}

interface CategoryCard {
  category: string;
  count: number;
  description: string;
  examples: string[];
}

interface CategoryGroup {
  label: string;
  description: string;
  categories: string[];
}

interface GroupedCategoryCards {
  label: string;
  description: string;
  count: number;
  cards: CategoryCard[];
}

interface ResourceGuardrail {
  label: string;
  detail: string;
}

interface ResourcePlanStep {
  title: string;
  detail: string;
}

interface FilteredResourceGroup {
  category: string;
  description: string;
  resources: Resource[];
}



@Component({
  selector: 'app-resources',
  templateUrl: './resources.component.html',
  styleUrls: ['./resources.component.scss']
})
export class ResourcesComponent {
  selectedCategory = 'All';
  searchTerm = '';
  readonly pageSize = 6;
  currentPage = 1;
  serviceDropdownOpen = false;

  private readonly serviceStyles: Record<string, ServiceStyle> = {
    All: { background: '#102a31', color: '#ffffff' },
    Appliances: { background: '#b45309', color: '#ffffff' },
    Foundation: { background: '#0f766e', color: '#ffffff' },
    Roofing: { background: '#9a3412', color: '#ffffff' },
    Electrical: { background: '#6d28d9', color: '#ffffff' },
    Plumbing: { background: '#1d4ed8', color: '#ffffff' },
    Pool: { background: '#0369a1', color: '#ffffff' },
    Painting: { background: '#be123c', color: '#ffffff' },
    Handyman: { background: '#4d7c0f', color: '#ffffff' },
    'General Contractors': { background: '#334155', color: '#ffffff' },
    'Pest Control': { background: '#166534', color: '#ffffff' },
    Septic: { background: '#155e75', color: '#ffffff' }
  };

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  @HostListener('document:click', ['$event'])
  closeServiceDropdownOnOutsideClick(event: MouseEvent): void {
    if (!this.serviceDropdownOpen) {
      return;
    }

    const target = event.target;

    const serviceMenu = this.elementRef.nativeElement.querySelector('.resource-service-menu');

    if (target instanceof Node && (!serviceMenu || !serviceMenu.contains(target))) {
      this.serviceDropdownOpen = false;
    }
  }

  private readonly semanticDimensions = [
    'appliance',
    'foundation',
    'roofing',
    'electrical',
    'plumbing',
    'pool',
    'painting',
    'generalContractor',
    'handyman',
    'pest',
    'septic',
    'water',
    'structure',
    'safety',
    'leak',
    'drainage',
    'finish',
    'repair'
  ];

  private readonly clientStoredServiceEmbeddings: Record<string, number[]> = {
    Appliances: this.semanticVector({ appliance: 1, electrical: 0.25, plumbing: 0.15, repair: 0.38 }),
    Foundation: this.semanticVector({ foundation: 1, structure: 0.82, drainage: 0.5, repair: 0.34 }),
    Roofing: this.semanticVector({ roofing: 1, water: 0.7, leak: 0.62, drainage: 0.48, repair: 0.28 }),
    Electrical: this.semanticVector({ electrical: 1, safety: 0.76, repair: 0.32 }),
    Plumbing: this.semanticVector({ plumbing: 1, water: 0.85, leak: 0.72, drainage: 0.44, repair: 0.26 }),
    Pool: this.semanticVector({ pool: 1, plumbing: 0.3, water: 0.55, safety: 0.24, repair: 0.2 }),
    Painting: this.semanticVector({ painting: 1, finish: 0.82, repair: 0.3 }),
    'General Contractors': this.semanticVector({ generalContractor: 1, structure: 0.58, finish: 0.42, repair: 0.58 }),
    Handyman: this.semanticVector({ handyman: 1, repair: 0.88, finish: 0.42 }),
    'Pest Control': this.semanticVector({ pest: 1, safety: 0.42, repair: 0.18 }),
    Septic: this.semanticVector({ septic: 1, plumbing: 0.38, water: 0.4, drainage: 0.45, safety: 0.22 })
  };

  private readonly categoryKeywordTerms: Record<string, string[]> = {
    Appliances: ['appliance', 'appliances', 'washer', 'dryer', 'dishwasher', 'oven', 'range', 'stove', 'microwave', 'refrigerator', 'fridge', 'disposal'],
    Foundation: ['foundation', 'foundations', 'slab', 'pier', 'beam', 'settling', 'settlement', 'structure', 'structural', 'repair'],
    Roofing: ['roof', 'roofer', 'roofing', 'shingle', 'flashing', 'chimney', 'gutter', 'leak'],
    Electrical: ['electric', 'electrical', 'panel', 'breaker', 'outlet', 'wire', 'wiring', 'light'],
    Plumbing: ['plumbing', 'plumber', 'pipe', 'toilet', 'sink', 'faucet', 'water', 'heater', 'drain'],
    Pool: ['pool', 'pools', 'spa', 'pump', 'filter', 'inspection', 'inspector'],
    Painting: ['paint', 'painting', 'painter', 'drywall', 'texture', 'stain', 'finish'],
    Handyman: ['handyman', 'handy', 'repair', 'maintenance', 'fix', 'punch', 'list'],
    'General Contractors': ['general', 'contractor', 'contractors', 'construction', 'builder', 'remodel', 'repair'],
    'Pest Control': ['pest', 'termite', 'bug', 'insect', 'rodent', 'wdi', 'control'],
    Septic: ['septic', 'tank', 'field', 'drainfield', 'wastewater', 'sewer', 'environmental']
  };

  private readonly semanticAliases: Array<{ terms: string[]; weights: Record<string, number> }> = [
    {
      terms: ['appliance', 'appliances', 'dishwasher', 'washer', 'dryer', 'oven', 'range', 'stove', 'refrigerator', 'fridge'],
      weights: { appliance: 1, electrical: 0.22, plumbing: 0.12, repair: 0.3 }
    },
    {
      terms: ['foundation', 'slab', 'settling', 'settlement', 'pier', 'beam', 'crack', 'movement', 'structural'],
      weights: { foundation: 1, structure: 0.62, repair: 0.24 }
    },
    {
      terms: ['roof', 'roofing', 'shingle', 'flashing', 'chimney', 'gutter', 'skylight', 'drip edge'],
      weights: { roofing: 1, water: 0.48, leak: 0.36, drainage: 0.32 }
    },
    {
      terms: ['electric', 'electrical', 'panel', 'breaker', 'outlet', 'receptacle', 'gfci', 'wire', 'wiring', 'light'],
      weights: { electrical: 1, safety: 0.58, repair: 0.24 }
    },
    {
      terms: ['plumbing', 'plumber', 'pipe', 'toilet', 'sink', 'faucet', 'water heater', 'drain', 'pressure'],
      weights: { plumbing: 1, water: 0.62, repair: 0.24 }
    },
    {
      terms: ['leak', 'moisture', 'water stain', 'water', 'drainage', 'drain', 'clog'],
      weights: { water: 0.88, leak: 0.82, plumbing: 0.48, roofing: 0.38, drainage: 0.44 }
    },
    {
      terms: ['pool', 'spa', 'pump', 'filter'],
      weights: { pool: 1, water: 0.42, plumbing: 0.2 }
    },
    {
      terms: ['paint', 'painting', 'painter', 'drywall', 'texture', 'stain', 'finish'],
      weights: { painting: 1, finish: 0.68, repair: 0.28 }
    },
    {
      terms: ['contractor', 'contractors', 'construction', 'builder', 'remodel', 'general contractor', 'general contractors'],
      weights: { generalContractor: 1, structure: 0.4, finish: 0.34, repair: 0.5 }
    },
    {
      terms: ['handyman', 'punch list', 'punch-list', 'repair', 'maintenance', 'fix', 'general repair'],
      weights: { handyman: 1, repair: 0.82, finish: 0.32 }
    },
    {
      terms: ['pest', 'termite', 'bug', 'insect', 'rodent', 'wdi', 'wood destroying'],
      weights: { pest: 1, safety: 0.22 }
    },
    {
      terms: ['septic', 'septic tank', 'drainfield', 'wastewater', 'sewer'],
      weights: { septic: 1, water: 0.34, drainage: 0.32, plumbing: 0.28 }
    }
  ];

  private readonly semanticSearchThreshold = 0.28;

  resourceGuardrails: ResourceGuardrail[] = [
    {
      label: 'Start local',
      detail: 'Use the list as a Central Texas starting point for inspection follow-up.'
    },
    {
      label: 'Verify first',
      detail: 'Confirm licensing, insurance, scope, pricing, reviews, and availability before hiring.'
    },
    {
      label: 'No endorsement',
      detail: 'Provider contacts are informational resources, not guarantees of fit or workmanship.'
    }
  ];

  resourceHeroCues = [
    'Filter by trade',
    'Search by trade, business, or phone',
    'Verify before hiring'
  ];

  categoryGroups: CategoryGroup[] = [
    {
      label: 'Major home systems',
      description: 'High-priority trades clients often need after a home inspection.',
      categories: ['Appliances', 'Foundation', 'Roofing', 'Electrical', 'Plumbing', 'Septic']
    },
    {
      label: 'Specialty checks',
      description: 'Targeted follow-up for pools, pests, and inspection-specific concerns.',
      categories: ['Pool', 'Pest Control']
    },
    {
      label: 'Repair and project support',
      description: 'Project support for punch-list items, surface repairs, and larger follow-up work.',
      categories: ['Painting', 'Handyman', 'General Contractors']
    }
  ];

  categoryDescriptions: Record<string, string> = {
    Appliances: 'Appliance inspection, service, and repair follow-up.',
    Foundation: 'Foundation movement, settling, drainage, and structural follow-up.',
    Roofing: 'Roof covering, flashing, leak, and weather-exposure follow-up.',
    Electrical: 'Electrical repair, panel, outlet, and safety item follow-up.',
    Plumbing: 'Fixture, supply, drain, water heater, and leak follow-up.',
    Pool: 'Pool equipment and pool-specific inspection follow-up.',
    Painting: 'Interior or exterior painting and finish repair follow-up.',
    Handyman: 'General repair, punch-list, and maintenance project support.',
    'General Contractors': 'Larger repair, construction, and project coordination follow-up.',
    'Pest Control': 'Wood-destroying insect, pest, and prevention follow-up.',
    Septic: 'Septic system, tank, and wastewater service follow-up.'
  };

  resources: Resource[] = [
    {
      displayName: 'Appliance Services:',
      businessName: 'Alpha and Omega',
      phone: '512-930-3067',
      category: 'Appliances',
    },
    {
      displayName: 'Foundation Services:',
      employeeName: 'Chris',
      businessName: 'Done Right Foundations',
      phone: '512-243-9991',
      category: 'Foundation',
    },
    {
      displayName: 'Foundation Services:',
      businessName: 'Centex Foundation Repair',
      phone: '512-444-5438',
      category: 'Foundation',
    },
    {
      displayName: 'Roofing Services:',
      employeeName: 'Jim McBride',
      businessName: 'Avanti Roofing',
      phone: '512-470-5442',
      category: 'Roofing',
    },
    {
      displayName: 'Electrical Services:',
      businessName: 'D Z Electrical Services LLC',
      phone: '512-508-1637',
      category: 'Electrical',
    },
    {
      displayName: 'Plumbing Services:',
      businessName: 'Brittain Plumbing',
      phone: '512-869-1331',
      category: 'Plumbing',
    },
    {
      displayName: 'Plumbing Services:',
      businessName: 'Stan\'s Heating and Plumbing',
      phone: '512-871-7837',
      category: 'Plumbing',
    },
    {
      displayName: 'Pool Inspection Services:',
      employeeName: 'Peter Batterton',
      businessName: 'POOL INSPECTOR',
      phone: '512-294-6214',
      category: 'Pool',
    },
    {
      displayName: 'Pool Inspection Services:',
      employeeName: 'Brent Bailey',
      businessName: 'A+ POOL INSPECTIONS',
      phone: '512-431-6268',
      category: 'Pool',
    },
    {
      displayName: 'Painting Services:',
      employeeName: 'Steven Montgomery',
      businessName: 'That 1 Painter',
      phone: '512-437-1427',
      category: 'Painting',
    },
    {
      displayName: 'Handyman Services:',
      employeeName: 'Marlon Saucedo',
      businessName: 'Super Handy Man',
      phone: '512-662-4317',
      category: 'Handyman',
    },
    {
      displayName: 'Handyman Services:',
      businessName: 'Cody\'s Handyman Services',
      phone: '512-431-0459',
      category: 'Handyman',
    },
    {
      displayName: 'General Contractor Services:',
      employeeName: 'Rocky',
      businessName: 'Don Boozer Construction',
      phone: '512-799-8189',
      category: 'General Contractors',
    },
    {
      displayName: 'Pest Control Services:',
      employeeName: 'Joe or Patrick',
      businessName: 'Georgetown Pest Control',
      phone: '512-966-6293',
      category: 'Pest Control',
    },
    {
      displayName: 'Pest Control Services:',
      employeeName: 'Lynn King',
      businessName: 'Pest Control',
      phone: '512-966-2847',
      category: 'Pest Control',
    },
    {
      displayName: 'Pest Control Services:',
      businessName: 'Stride Pest Control',
      phone: '512-777-1339',
      category: 'Pest Control',
    },
    {
      displayName: 'Septic Services:',
      employeeName: 'Matt Carter',
      businessName: 'Carter Septic Services',
      phone: '512-992-5287',
      category: 'Septic',
    },
    {
      displayName: 'Septic Services:',
      employeeName: 'Gerald',
      businessName: 'C&R Environmental Services',
      phone: '902-292-7627',
      category: 'Septic',
    },
  ];

  links = [
    {
      label: 'Austin Board of Realtors',
      url: 'https://www.abor.com/',
      description: 'Local real estate association resource.'
    },
    {
      label: 'US Consumer Product Safety Commission',
      url: 'https://www.cpsc.gov/',
      description: 'Safety recall and consumer product information.'
    },
    {
      label: 'Environmental Protection Agency',
      url: 'https://www.epa.gov/',
      description: 'Environmental and home health guidance.'
    },
    {
      label: 'Texas Real Estate Commission',
      url: 'https://www.trec.texas.gov/',
      description: 'Texas real estate and inspector licensing information.'
    },
    {
      label: 'Structural Pest Control Board',
      url: 'https://www.texasagriculture.gov/RegulatoryPrograms/Pesticides/StructuralPestControlService.aspx',
      description: 'Texas structural pest control licensing resource.'
    },
    {
      label: 'Mesothelioma Information',
      url: 'https://www.pleuralmesothelioma.com/cancer/',
      description: 'General asbestos-related disease education.'
    },
  ];

  resourceReviewSteps = [
    'Confirm licensing and insurance',
    'Compare scope, price, and timing',
    'Read current reviews',
    'Choose the provider that fits your project'
  ];

  resourcePlanSteps: ResourcePlanStep[] = [
    {
      title: 'Match the trade to the report note',
      detail: 'Use the category cards or search field to narrow the provider list to the system or repair type.'
    },
    {
      title: 'Compare more than one contact',
      detail: 'Use the first few contacts as a shortlist, then compare scope, timing, pricing, and current reviews.'
    },
    {
      title: 'Verify before hiring',
      detail: 'Confirm licensing, insurance, availability, and fit directly with the provider before work begins.'
    }
  ];

  get categories(): string[] {
    return ['All', ...Array.from(new Set(this.resources.map((resource) => resource.category || 'Other')))];
  }

  get categoryCounts(): Record<string, number> {
    return this.resources.reduce<Record<string, number>>((counts, resource) => {
      const category = resource.category || 'Other';
      counts[category] = (counts[category] || 0) + 1;
      return counts;
    }, {});
  }

  get categoryCards(): CategoryCard[] {
    return this.categories
      .filter((category) => category !== 'All')
      .map((category) => {
        const resources = this.resources.filter((resource) => resource.category === category);

        return {
          category,
          count: resources.length,
          description: this.categoryDescriptions[category] || 'Local service provider contacts for client follow-up.',
          examples: resources
            .map((resource) => resource.businessName?.trim() || resource.employeeName?.trim() || this.serviceLabel(resource))
            .filter(Boolean)
            .slice(0, 2)
        };
      });
  }

  get groupedCategoryCards(): GroupedCategoryCards[] {
    const cardsByCategory = new Map(this.categoryCards.map((card) => [card.category, card]));
    const groupedCategories = new Set<string>();

    const groups = this.categoryGroups
      .map((group) => {
        const cards = group.categories
          .map((category) => cardsByCategory.get(category))
          .filter((card): card is CategoryCard => Boolean(card));

        cards.forEach((card) => groupedCategories.add(card.category));

        return {
          label: group.label,
          description: group.description,
          count: cards.reduce((total, card) => total + card.count, 0),
          cards
        };
      })
      .filter((group) => group.cards.length > 0);

    const remainingCards = this.categoryCards.filter((card) => !groupedCategories.has(card.category));

    if (remainingCards.length) {
      groups.push({
        label: 'Additional local contacts',
        description: 'Other helpful providers available for client follow-up.',
        count: remainingCards.reduce((total, card) => total + card.count, 0),
        cards: remainingCards
      });
    }

    return groups;
  }

  get filteredResourceGroups(): FilteredResourceGroup[] {
    const filteredResources = this.filteredResources;
    const orderedCategories = [
      ...this.categoryGroups.flatMap((group) => group.categories),
      ...this.categories.filter((category) => category !== 'All')
    ];
    const uniqueCategories = Array.from(new Set(orderedCategories));

    const groups = uniqueCategories
      .map((category) => {
        const resources = filteredResources.filter((resource) => (resource.category || 'Other') === category);

        return {
          category,
          description: this.categoryDescriptions[category] || 'Local service provider contacts for client follow-up.',
          resources
        };
      })
      .filter((group) => group.resources.length > 0);

    if (!this.trimmedSearchTerm) {
      return groups;
    }

    return groups.sort((first, second) => {
      return filteredResources.indexOf(first.resources[0]) - filteredResources.indexOf(second.resources[0]);
    });
  }

  get activeCategoryDescription(): string {
    if (this.selectedCategory === 'All') {
      return 'Browse every provider or narrow the list by trade.';
    }

    return this.categoryDescriptions[this.selectedCategory] || 'Local service provider contacts for client follow-up.';
  }

  get filteredResources(): Resource[] {
    const normalizedSearch = this.normalizeSearchText(this.searchTerm);
    const normalizedPhoneSearch = this.normalizePhone(this.searchTerm);
    const queryEmbedding = this.querySemanticVector(normalizedSearch);
    let resources = this.resources;

    if (this.selectedCategory !== 'All') {
      resources = resources.filter((resource) => resource.category === this.selectedCategory);
    }

    if (!normalizedSearch) {
      return resources;
    }

    return resources
      .map((resource) => ({
        resource,
        score: this.resourceSearchScore(resource, normalizedSearch, normalizedPhoneSearch, queryEmbedding)
      }))
      .filter((result) => result.score > 0)
      .sort((first, second) => {
        return second.score - first.score || this.resources.indexOf(first.resource) - this.resources.indexOf(second.resource);
      })
      .map((result) => result.resource);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredResources.length / this.pageSize));
  }

  get clampedCurrentPage(): number {
    return Math.min(Math.max(this.currentPage, 1), this.totalPages);
  }

  get pagedResources(): Resource[] {
    const start = (this.clampedCurrentPage - 1) * this.pageSize;
    return this.filteredResources.slice(start, start + this.pageSize);
  }

  get hasMultiplePages(): boolean {
    return this.filteredResources.length > this.pageSize;
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, index) => index + 1);
  }

  get visiblePageStart(): number {
    if (this.filteredResources.length === 0) {
      return 0;
    }

    return (this.clampedCurrentPage - 1) * this.pageSize + 1;
  }

  get visiblePageEnd(): number {
    return Math.min(this.clampedCurrentPage * this.pageSize, this.filteredResources.length);
  }

  get pageSummary(): string {
    if (this.filteredResources.length === 0) {
      return 'No contacts shown';
    }

    return `Showing ${this.visiblePageStart}-${this.visiblePageEnd} of ${this.filteredResources.length}`;
  }

  get activeSummary(): string {
    const categoryLabel = this.selectedCategory === 'All' ? 'all categories' : this.selectedCategory;
    const resultLabel = this.filteredResources.length === 1 ? 'provider' : 'providers';
    return `${this.filteredResources.length} ${resultLabel} shown for ${categoryLabel}`;
  }

  get searchSummary(): string {
    return this.searchTerm.trim() || 'No search entered';
  }

  get trimmedSearchTerm(): string {
    return this.searchTerm.trim();
  }

  get hasActiveFilters(): boolean {
    return this.selectedCategory !== 'All' || this.trimmedSearchTerm.length > 0;
  }

  get selectedCategoryCount(): number {
    return this.selectedCategory === 'All'
      ? this.resources.length
      : this.categoryCounts[this.selectedCategory] || 0;
  }

  get resourcePlanTitle(): string {
    if (this.selectedCategory === 'All' && !this.trimmedSearchTerm) {
      return 'Choose the trade connected to the inspection note.';
    }

    if (this.selectedCategory !== 'All') {
      return `${this.selectedCategory} follow-up blueprint`;
    }

    return `Search results for "${this.trimmedSearchTerm}"`;
  }

  get resourcePlanSummary(): string {
    if (this.filteredResources.length === 0) {
      return 'No contacts match the current filters. Clear the plan or search another service, business, contact, or phone number.';
    }

    if (this.selectedCategory === 'All' && !this.trimmedSearchTerm) {
      return 'Browse the grouped provider cards, choose a trade group, then compare a few local contacts before hiring.';
    }

    return `${this.filteredResources.length} contacts are ready to compare. Use the shortlist below as a starting point, then independently verify each provider.`;
  }

  get resourcePlanContacts(): Resource[] {
    return this.filteredResources.slice(0, 3);
  }

  private semanticVector(weights: Record<string, number>): number[] {
    return this.semanticDimensions.map((dimension) => weights[dimension] || 0);
  }

  private normalizeSearchText(value: string): string {
    return value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['\u2019]/g, '')
      .replace(/[^a-z0-9+]+/gi, ' ')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private normalizePhone(value = ''): string {
    return value.replace(/\D/g, '');
  }

  private querySemanticVector(normalizedSearch: string): number[] {
    const vector = this.semanticVector({});

    if (!normalizedSearch) {
      return vector;
    }

    this.semanticAliases.forEach((alias) => {
      const hasMatch = alias.terms.some((term) => normalizedSearch.includes(term));

      if (!hasMatch) {
        return;
      }

      const aliasVector = this.semanticVector(alias.weights);
      aliasVector.forEach((value, index) => {
        vector[index] += value;
      });
    });

    return vector;
  }

  private resourceEmbedding(resource: Resource): number[] {
    return this.clientStoredServiceEmbeddings[resource.category || ''] || this.semanticVector({});
  }

  private resourceSearchScore(
    resource: Resource,
    normalizedSearch: string,
    normalizedPhoneSearch: string,
    queryEmbedding: number[]
  ): number {
    const keywordScore = this.matchesResourceKeyword(resource, normalizedSearch, normalizedPhoneSearch) ? 2 : 0;
    const fuzzyScore = this.fuzzyKeywordScore(resource, normalizedSearch);
    const semanticScore = this.cosineSimilarity(queryEmbedding, this.resourceEmbedding(resource));
    const bestKeywordScore = Math.max(keywordScore, fuzzyScore);

    if (semanticScore < this.semanticSearchThreshold) {
      return bestKeywordScore;
    }

    return Math.max(bestKeywordScore, semanticScore);
  }

  private matchesResourceKeyword(resource: Resource, normalizedSearch: string, normalizedPhoneSearch: string): boolean {
    const searchableText = this.searchableResourceText(resource);
    const compactSearchableText = searchableText.replace(/\s+/g, '');
    const compactSearch = normalizedSearch.replace(/\s+/g, '');

    const textMatch = searchableText.includes(normalizedSearch) || compactSearchableText.includes(compactSearch);

    if (textMatch) {
      return true;
    }

    return normalizedPhoneSearch.length > 0 && this.normalizePhone(resource.phone).includes(normalizedPhoneSearch);
  }

  private searchableResourceText(resource: Resource): string {
    return [
      resource.displayName,
      resource.businessName,
      resource.employeeName,
      resource.phone,
      resource.email,
      resource.category,
      this.categoryDescriptions[resource.category || '']
    ]
      .filter((value): value is string => Boolean(value))
      .map((value) => this.normalizeSearchText(value))
      .join(' ');
  }

  private resourceSearchTerms(resource: Resource): string[] {
    const category = resource.category || '';
    const terms = [
      ...this.searchableResourceText(resource).split(' '),
      ...(this.categoryKeywordTerms[category] || [])
    ];

    return Array.from(new Set(terms.map((term) => this.normalizeSearchText(term)).filter((term) => term.length > 1)));
  }

  private fuzzyKeywordScore(resource: Resource, normalizedSearch: string): number {
    const queryTerms = normalizedSearch
      .split(' ')
      .filter((term) => term.length > 1 && !/^\d+$/.test(term));

    if (!queryTerms.length) {
      return 0;
    }

    const resourceTerms = this.resourceSearchTerms(resource);
    const termScores = queryTerms.map((queryTerm) => this.bestFuzzyTermScore(queryTerm, resourceTerms));
    const everyTermMatches = termScores.every((score, index) => score >= this.fuzzyThreshold(queryTerms[index]));

    if (!everyTermMatches) {
      return 0;
    }

    const averageScore = termScores.reduce((total, score) => total + score, 0) / termScores.length;
    return averageScore * 1.5;
  }

  private bestFuzzyTermScore(queryTerm: string, resourceTerms: string[]): number {
    return resourceTerms.reduce((bestScore, resourceTerm) => {
      return Math.max(bestScore, this.fuzzyTermScore(queryTerm, resourceTerm));
    }, 0);
  }

  private fuzzyTermScore(queryTerm: string, resourceTerm: string): number {
    if (queryTerm === resourceTerm) {
      return 1;
    }

    if (queryTerm.length <= 2 || resourceTerm.length <= 2) {
      return 0;
    }

    if (resourceTerm.includes(queryTerm) || queryTerm.includes(resourceTerm)) {
      return Math.min(queryTerm.length, resourceTerm.length) / Math.max(queryTerm.length, resourceTerm.length);
    }

    const distance = this.levenshteinDistance(queryTerm, resourceTerm);
    return 1 - distance / Math.max(queryTerm.length, resourceTerm.length);
  }

  private fuzzyThreshold(term: string): number {
    if (term.length <= 3) {
      return 0.86;
    }

    if (term.length <= 5) {
      return 0.72;
    }

    return 0.68;
  }

  private levenshteinDistance(first: string, second: string): number {
    const previousRow = Array.from({ length: second.length + 1 }, (_, index) => index);

    for (let firstIndex = 0; firstIndex < first.length; firstIndex++) {
      const currentRow = [firstIndex + 1];

      for (let secondIndex = 0; secondIndex < second.length; secondIndex++) {
        const insertCost = currentRow[secondIndex] + 1;
        const deleteCost = previousRow[secondIndex + 1] + 1;
        const replaceCost = previousRow[secondIndex] + (first[firstIndex] === second[secondIndex] ? 0 : 1);
        currentRow.push(Math.min(insertCost, deleteCost, replaceCost));
      }

      previousRow.splice(0, previousRow.length, ...currentRow);
    }

    return previousRow[second.length];
  }

  private cosineSimilarity(firstVector: number[], secondVector: number[]): number {
    const dotProduct = firstVector.reduce((total, value, index) => total + value * (secondVector[index] || 0), 0);
    const firstMagnitude = Math.sqrt(firstVector.reduce((total, value) => total + value * value, 0));
    const secondMagnitude = Math.sqrt(secondVector.reduce((total, value) => total + value * value, 0));

    if (!firstMagnitude || !secondMagnitude) {
      return 0;
    }

    return dotProduct / (firstMagnitude * secondMagnitude);
  }

  selectCategory(category: string): void {
    this.selectedCategory = category;
    this.currentPage = 1;
  }

  toggleServiceDropdown(): void {
    this.serviceDropdownOpen = !this.serviceDropdownOpen;
  }

  selectCategoryFromDropdown(category: string): void {
    this.selectCategory(category);
    this.serviceDropdownOpen = false;
  }

  selectCategoryFromEvent(event: Event): void {
    this.selectCategory((event.target as HTMLSelectElement).value);
  }

  updateSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.currentPage = 1;
  }

  clearFilters(): void {
    this.selectedCategory = 'All';
    this.searchTerm = '';
    this.currentPage = 1;
  }

  setPage(page: number): void {
    this.currentPage = Math.min(Math.max(page, 1), this.totalPages);
  }

  nextPage(): void {
    this.setPage(this.currentPage + 1);
  }

  previousPage(): void {
    this.setPage(this.currentPage - 1);
  }

  serviceOptionLabel(category: string): string {
    return category === 'All' ? 'All services' : category;
  }

  serviceBackground(category = 'All'): string {
    return (this.serviceStyles[category] || this.serviceStyles['All']).background;
  }

  serviceTextColor(category = 'All'): string {
    return (this.serviceStyles[category] || this.serviceStyles['All']).color;
  }

  serviceLabel(resource: Resource): string {
    return (resource.displayName || resource.category || 'Service provider').replace(':', '').trim();
  }

  contactLabel(resource: Resource): string {
    return resource.employeeName?.trim() || '';
  }

  hasContactName(resource: Resource): boolean {
    return Boolean(resource.employeeName?.trim());
  }

  businessLabel(resource: Resource): string {
    return resource.businessName?.trim() || 'Independent provider';
  }

  providerName(resource: Resource): string {
    return resource.businessName?.trim() || resource.employeeName?.trim() || 'Independent provider';
  }

  providerSubline(resource: Resource): string {
    const contact = resource.employeeName?.trim();
    const business = resource.businessName?.trim();

    if (contact && business) {
      return `Contact: ${contact}`;
    }

    if (contact) {
      return 'Independent provider contact';
    }

    return 'Business contact listed';
  }

  phoneHref(phone: string): string {
    return `tel:${phone.replace(/\D/g, '')}`;
  }

}
